import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import api from '../lib/api';
import { useEventBusStore } from '../stores/event-bus.store';

export const Route = createFileRoute('/assinaturas')({
  component: AssinaturasPage,
});

type SubscriptionSummaryItem = {
  workspaceId: string;
  workspaceName: string;
  plan: {
    id: string;
    name: string;
    price: number;
    currency: string;
    maxClients: number;
    maxProcesses: number;
    maxUsers: number;
    storageLimit: number;
    isFree: boolean;
  } | null;
  currentPeriodEnd: string | null;
  subscriptionStatus: string | null;
  cancelAtPeriodEnd: boolean;
  isPaid: boolean;
  usage: {
    clients: number;
    processes: number;
    users: number;
    storage: { used: number; limit: number };
  };
};

type AvailablePlan = {
  id: string;
  name: string;
  description: string;
  isFree: boolean;
  billingCycle: string | null;
  price: number | null;
  currency: string;
  maxClients: number;
  maxProcesses: number;
  maxUsers: number;
  maxStorageBytes: number;
};

function AssinaturasPage() {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<SubscriptionSummaryItem | null>(null);
  const [upgradeTarget, setUpgradeTarget] = useState<SubscriptionSummaryItem | null>(null);
  const queryClient = useQueryClient();
  const addToast = useEventBusStore((state) => state.addToast);

  const upgradeMutation = useMutation({
    mutationFn: async ({
      workspaceId,
      planId,
    }: {
      workspaceId: string;
      planId: string;
    }) => {
      const res = await api.post(
        `/subscriptions/workspaces/${workspaceId}/upgrade`,
        { planId },
      );
      return res.data.data as { success?: true; url?: string };
    },
    onSuccess: (data, { workspaceId }) => {
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['subscriptions-summary'] });
        addToast('Plano alterado com sucesso.', 'success');
        setUpgradeTarget(null);
      }
    },
    onError: (err: any) => {
      addToast(err.response?.data?.message ?? 'Erro ao alterar plano', 'error');
    },
  });

  const { data: availablePlans } = useQuery<AvailablePlan[]>({
    queryKey: ['available-plans'],
    queryFn: async () => {
      const res = await api.get('/plans/available');
      return res.data.data;
    },
    enabled: !!upgradeTarget,
  });

  const cancelMutation = useMutation({
    mutationFn: async (workspaceId: string) => {
      await api.post(
        `/subscriptions/workspaces/${workspaceId}/subscription/cancel`,
        {},
        { params: { immediate: 'false' } },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions-summary'] });
      addToast('Assinatura cancelada. O acesso permanece até o vencimento.', 'success');
      setCancelTarget(null);
    },
    onError: (err: any) => {
      addToast(err.response?.data?.message ?? 'Erro ao cancelar assinatura', 'error');
    },
  });

  const { data: list, isLoading } = useQuery<SubscriptionSummaryItem[]>({
    queryKey: ['subscriptions-summary'],
    queryFn: async () => {
      const res = await api.get('/workspaces/subscriptions-summary');
      const payload = res.data?.data;
      if (Array.isArray(payload)) return payload;
      return payload?.items || [];
    },
  });

  const formatCurrency = (value: number, currency: string) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency || 'BRL',
    }).format(value);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div
        className="rounded-[15px] p-6 sm:p-8 bg-white dark:bg-gray-800"
        style={{ boxShadow: '0 0 15px rgba(0,0,0,0.1)' }}
      >
        <h1
          className="text-base font-semibold mb-1"
          style={{ color: '#06141B', fontSize: 16 }}
        >
          Assinaturas
        </h1>
        <p className="text-sm mb-6" style={{ color: '#4A5C6A', fontSize: 12 }}>
          Resumo de assinaturas por workspace
        </p>

        {isLoading ? (
          <div className="text-center py-8 text-gray-500">Carregando...</div>
        ) : !list?.length ? (
          <div className="text-center py-8 text-gray-500">
            Nenhum workspace encontrado.
          </div>
        ) : (
          <div className="space-y-2">
            {list.map((item, idx) => (
              <div
                key={item.workspaceId}
                className={`rounded-[10px] border border-gray-200 dark:border-gray-600 overflow-hidden ${
                  idx % 2 === 0 ? 'bg-gray-50/50 dark:bg-gray-700/30' : 'bg-white dark:bg-gray-800'
                }`}
              >
                <div className="flex items-center justify-between gap-4 p-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#06141B] dark:text-gray-100 truncate">
                      {item.workspaceName}
                    </p>
                    <p className="text-sm text-[#4A5C6A] dark:text-gray-400">
                      {item.plan?.name ?? 'Sem plano'} · Vencimento:{' '}
                      {formatDate(item.currentPeriodEnd)}
                      {item.plan && !item.plan.isFree && (
                        <> · {formatCurrency(item.plan.price / 100, item.plan.currency)}</>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {item.isPaid &&
                      item.subscriptionStatus === 'active' &&
                      !item.cancelAtPeriodEnd && (
                        <button
                          type="button"
                          onClick={() => setCancelTarget(item)}
                          className="rounded-[10px] px-3 py-2 border border-red-600 text-red-600 dark:border-red-400 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-sm font-medium"
                        >
                          Cancelar assinatura
                        </button>
                      )}
                    <button
                      type="button"
                      onClick={() => setUpgradeTarget(item)}
                      className="rounded-[10px] px-3 py-2 border border-[#06141B] text-[#06141B] dark:border-gray-400 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm font-medium"
                    >
                      Upgrade de plano
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedId(expandedId === item.workspaceId ? null : item.workspaceId)
                      }
                      className="rounded-[10px] px-3 py-2 border border-[#06141B] text-[#06141B] dark:border-gray-400 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm font-medium"
                    >
                      {expandedId === item.workspaceId ? '▲' : '▼'}
                    </button>
                  </div>
                </div>
                {expandedId === item.workspaceId && (
                  <div className="px-4 pb-4 pt-0 border-t border-gray-200 dark:border-gray-600 mt-0 pt-4">
                    <p className="text-xs text-[#4A5C6A] dark:text-gray-400 mb-2">
                      Uso: Clientes {item.usage.clients}
                      {item.plan ? ` / ${item.plan.maxClients}` : ''} · Processos{' '}
                      {item.usage.processes}
                      {item.plan ? ` / ${item.plan.maxProcesses}` : ''} · Usuários{' '}
                      {item.usage.users}
                      {item.plan ? ` / ${item.plan.maxUsers}` : ''} · Armazenamento{' '}
                      {formatBytes(item.usage.storage.used)} /{' '}
                      {formatBytes(item.usage.storage.limit)}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de upgrade de plano */}
      {upgradeTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black bg-opacity-70"
            onClick={() => !upgradeMutation.isPending && setUpgradeTarget(null)}
          />
          <div className="relative bg-white dark:bg-gray-800 rounded-[15px] shadow-xl max-w-lg w-full mx-4 z-10 p-6 max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-[#06141B] dark:text-gray-100 mb-1">
              Alterar plano
            </h3>
            <p className="text-sm text-[#4A5C6A] dark:text-gray-400 mb-4">
              {upgradeTarget.workspaceName}
            </p>
            {!availablePlans ? (
              <p className="text-sm text-gray-500">Carregando planos...</p>
            ) : (() => {
              const otherPlans = availablePlans.filter((p) => p.id !== upgradeTarget.plan?.id);
              return otherPlans.length === 0 ? (
                <p className="text-sm text-[#4A5C6A] dark:text-gray-400 py-2">
                  Nenhum outro plano disponível no momento.
                </p>
              ) : (
              <ul className="space-y-2">
                {otherPlans.map((plan) => (
                    <li key={plan.id}>
                      <button
                        type="button"
                        onClick={() =>
                          upgradeMutation.mutate({
                            workspaceId: upgradeTarget.workspaceId,
                            planId: plan.id,
                          })
                        }
                        disabled={upgradeMutation.isPending}
                        className="w-full rounded-[10px] border border-gray-200 dark:border-gray-600 p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 flex justify-between items-center"
                      >
                        <span className="font-medium text-[#06141B] dark:text-gray-100">
                          {plan.name}
                        </span>
                        <span className="text-sm text-[#4A5C6A] dark:text-gray-400">
                          {plan.isFree
                            ? 'Grátis'
                            : formatCurrency((plan.price ?? 0) / 100, plan.currency)}
                        </span>
                      </button>
                    </li>
                  ))}
              </ul>
              );
            })()}
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setUpgradeTarget(null)}
                disabled={upgradeMutation.isPending}
                className="rounded-[10px] px-4 py-2 border border-[#06141B] text-[#06141B] dark:border-gray-400 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmação de cancelamento */}
      {cancelTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black bg-opacity-70"
            onClick={() => !cancelMutation.isPending && setCancelTarget(null)}
            aria-hidden
          />
          <div className="relative bg-white dark:bg-gray-800 rounded-[15px] shadow-xl max-w-md w-full mx-4 z-10 p-6">
            <h3 className="text-lg font-semibold text-[#06141B] dark:text-gray-100 mb-2">
              Cancelar assinatura
            </h3>
            <p className="text-sm text-[#4A5C6A] dark:text-gray-400 mb-4">
              O usuário terá acesso ao workspace até o vencimento. Após essa data, perderá o acesso ao
              workspace e deverá remover as informações e arquivos antes do prazo.
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
              Workspace: <strong>{cancelTarget.workspaceName}</strong>
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setCancelTarget(null)}
                className="rounded-[10px] px-4 py-2 border border-[#06141B] text-[#06141B] dark:border-gray-400 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Manter
              </button>
              <button
                type="button"
                onClick={() => cancelMutation.mutate(cancelTarget.workspaceId)}
                disabled={cancelMutation.isPending}
                className="rounded-[10px] px-4 py-2 bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {cancelMutation.isPending ? 'Cancelando...' : 'Confirmar cancelamento'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
